import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Check, Copy, LifeBuoy, Phone, Plus, Server, Users, Volume2, X } from 'lucide-react';
import type { Channel } from '@spawnwire/shared';
import { Avatar, Badge, clsx, ContextMenu, IconButton, SystemTag, type MenuEntry } from '../../components/ui';
import { http } from '../../lib/api';
import { ackChannel, attempt } from '../../lib/data';
import { onReady } from '../../lib/gateway';
import { copyText, useAppRoute, useIsMobile } from '../../lib/hooks';
import { NameText } from '../../lib/nameFonts';
import { joinVoice } from '../../lib/voice';
import { dmPartner, isUnread, mentionCount, privateChannelName, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { useMinecraft } from '../minecraft/store';
import { support, useSupport } from '../support/store';
import { MobileActiveCalls, MobileDmRow, MobileMessagesHeader, MobileShortcuts } from './MobileHome';

export function HomeSidebar() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { channelId: activeId, servers: onServers, support: onSupport } = useAppRoute();
  const runningServers = useMinecraft((s) => s.overview?.servers.filter((x) => x.state === 'running').length ?? 0);
  const supportReplies = useSupport((s) => s.tickets?.filter((t) => t.unread).length ?? 0);
  const channels = useStore((s) => s.channels);
  const presences = useStore((s) => s.presences);
  const relationships = useStore((s) => s.relationships);
  const voiceStates = useStore((s) => s.voiceStates);
  useStore((s) => s.readStates);
  useStore((s) => s.users);

  const list = useMemo(
    () =>
      Object.values(channels)
        .filter((c) => c.type === 'dm' || c.type === 'group_dm')
        .sort((a, b) => (b.lastMessageId ?? b.createdAt).localeCompare(a.lastMessageId ?? a.createdAt)),
    [channels],
  );
  // For the badge on Support when staff have replied. Replies after this arrive over the socket.
  useEffect(() => {
    if (!useSupport.getState().tickets) void support.load();
    const off = onReady(() => void support.load());
    return () => {
      off();
    };
  }, []);

  const pending = Object.values(relationships).filter((r) => r.type === 'incoming').length;
  const inVoice = useMemo(() => new Set(Object.values(voiceStates).flatMap((m) => Object.keys(m))), [voiceStates]);
  const partnerInVoice = (userId: string) => inVoice.has(userId);

  const go = (to: string) => {
    navigate(to);
    ui.setMobilePane('main');
  };

  /** Right-click (or long-press) menu for a conversation. */
  const menuFor = (c: Channel): MenuEntry[] => {
    const state = useStore.getState();
    const active = activeId === c.id;
    return [
      { label: 'Mark as read', icon: Check, onSelect: () => ackChannel(c) },
      { label: 'Start call', icon: Phone, onSelect: () => void joinVoice(c.id).then(() => go(`/app/@me/${c.id}`)) },
      { label: 'Copy ID', icon: Copy, onSelect: () => void copyText(c.id) },
      {
        label: c.type === 'dm' ? 'Close conversation' : 'Leave group',
        icon: X,
        danger: c.type === 'group_dm',
        separatorBefore: true,
        onSelect: () =>
          c.type === 'dm'
            ? void attempt(() => http.post(`/channels/${c.id}/close`)).then(() => active && navigate('/app/@me'))
            : ui.confirm({
                title: `Leave ${privateChannelName(state, c)}?`,
                body: 'You will not be able to rejoin unless someone adds you back.',
                confirmLabel: 'Leave group',
                danger: true,
                onConfirm: async () => {
                  await http.del(`/channels/${c.id}/recipients/${state.me!.id}`);
                  navigate('/app/@me');
                },
              }),
      },
    ];
  };

  // Phones get a layout of their own: a big "Messages" heading, quick actions and roomier conversation rows.
  if (isMobile) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <MobileMessagesHeader />
        <MobileShortcuts onFriends={!activeId && !onServers && !onSupport} onServers={onServers} onSupport={onSupport} supportReplies={supportReplies} go={go} />
        <nav aria-label="Direct messages" className="scroll-thin min-h-0 flex-1 overflow-y-auto pb-3 pt-1">
          <MobileActiveCalls go={go} />
          {list.length === 0 && <p className="px-4 py-6 text-center text-sm text-faint">Conversations you start will show up here.</p>}
          <ul className="space-y-0.5 px-2">
            {list.map((c) => (
              <li key={c.id}>
                <ContextMenu entries={menuFor(c)}>
                  <div>
                    <MobileDmRow channel={c} active={activeId === c.id} onOpen={() => go(`/app/@me/${c.id}`)} />
                  </div>
                </ContextMenu>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-12 shrink-0 items-center border-b border-line px-2.5 shadow-sm">
        <button
          type="button"
          onClick={() => ui.open({ kind: 'quickSwitcher' })}
          className="h-8 w-full rounded-lg border border-line bg-rail px-2 text-center text-[13px] font-medium text-muted hover:text-fg"
        >
          Find or start a conversation
        </button>
      </div>
      <nav aria-label="Direct messages" className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
        <button
          type="button"
          onClick={() => {
            navigate('/app/@me');
            ui.setMobilePane('main');
          }}
          className={clsx('flex h-10 w-full items-center gap-3 rounded-md px-3 text-[15px] font-medium', !activeId && !onServers && !onSupport ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}
        >
          <Users className="size-5" aria-hidden />
          <span className="flex-1 text-left">Friends</span>
          <Badge count={pending} />
        </button>
        <button
          type="button"
          onClick={() => {
            navigate('/app/servers');
            ui.setMobilePane('main');
          }}
          aria-current={onServers ? 'page' : undefined}
          className={clsx('mt-0.5 flex h-10 w-full items-center gap-3 rounded-md px-3 text-[15px] font-medium', onServers ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}
        >
          <Server className="size-5" aria-hidden />
          <span className="flex-1 text-left">Game Servers</span>
          {runningServers > 0 && (
            <span className="flex items-center gap-1 text-xs text-success" title={`${runningServers} running`}>
              <span className="size-2 rounded-full bg-success" aria-hidden /> {runningServers}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            navigate('/app/support');
            ui.setMobilePane('main');
          }}
          aria-current={onSupport ? 'page' : undefined}
          className={clsx('mt-0.5 flex h-10 w-full items-center gap-3 rounded-md px-3 text-[15px] font-medium', onSupport ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}
        >
          <LifeBuoy className="size-5" aria-hidden />
          <span className="flex-1 text-left">Support</span>
          {supportReplies > 0 && (
            <span
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white"
              aria-label={`${supportReplies} new support ${supportReplies === 1 ? 'reply' : 'replies'}`}
            >
              {supportReplies}
            </span>
          )}
        </button>

        <div className="mt-4 flex items-center justify-between px-2 pb-1">
          <span className="text-[13px] font-medium text-muted">Direct Messages</span>
          <IconButton icon={Plus} label="New group conversation" size="sm" onClick={() => ui.open({ kind: 'newGroup' })} />
        </div>

        {list.length === 0 && <p className="px-2 py-3 text-sm text-faint">Conversations you start will show up here.</p>}

        <ul className="space-y-px">
          {list.map((c) => {
            const state = useStore.getState();
            const partner = c.type === 'dm' ? dmPartner(state, c) : null;
            const name = privateChannelName(state, c);
            const unread = isUnread(state, c);
            const mentions = mentionCount(state, c.id);
            const active = activeId === c.id;
            const inCall = Object.keys(voiceStates[c.id] ?? {}).length > 0;
            return (
              <li key={c.id}>
                <ContextMenu entries={menuFor(c)}>
                  <div
                    role="link"
                    tabIndex={0}
                    aria-label={`${name}${unread ? ', unread' : ''}${mentions ? `, ${mentions} mentions` : ''}`}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => {
                      navigate(`/app/@me/${c.id}`);
                      ui.setMobilePane('main');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/app/@me/${c.id}`)}
                    className={clsx(
                      'group flex h-11 cursor-pointer items-center gap-3 rounded-md px-2',
                      active ? 'bg-active text-fg' : unread ? 'text-fg hover:bg-hover' : 'text-muted hover:bg-hover hover:text-fg',
                    )}
                  >
                    {partner ? (
                      <Avatar user={partner} size={32} status={presences[partner.id] ?? 'offline'} />
                    ) : (
                      <span className="flex size-8 items-center justify-center rounded-full bg-accent-soft text-accent">
                        <Users className="size-4" aria-hidden />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className={clsx('block truncate text-[15px]', unread && 'font-semibold')}>
                        <NameText font={partner?.nameFont}>{name}</NameText>
                        {partner?.system && <SystemTag />}
                      </span>
                      {c.type === 'group_dm' && <span className="block truncate text-xs text-faint">{c.recipients?.length ?? 0} members</span>}
                      {partner && partnerInVoice(partner.id) && (
                        <span className="flex items-center gap-1 truncate text-xs text-success">
                          <Volume2 className="size-3" aria-hidden /> In voice
                        </span>
                      )}
                    </span>
                    {inCall && <Phone className="size-4 text-success" aria-label="Call in progress" />}
                    <Badge count={mentions} />
                    {c.type === 'dm' && (
                      <button
                        type="button"
                        aria-label="Close conversation"
                        className="hidden rounded p-0.5 text-faint hover:text-fg group-hover:block"
                        onClick={(e) => {
                          e.stopPropagation();
                          void attempt(() => http.post(`/channels/${c.id}/close`)).then(() => active && navigate('/app/@me'));
                        }}
                      >
                        <X className="size-4" />
                      </button>
                    )}
                  </div>
                </ContextMenu>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
